import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  Modal,
  InputGroup,
  ProgressBar,
} from "react-bootstrap";
import {
  FaUser,
  FaLock,
  FaEye,
  FaEyeSlash,
  FaCheckCircle,
  FaExclamationTriangle,
} from "react-icons/fa";
import jwt_decode from "jwt-decode";
import SparkApi from "../api/SparkApi";
import PubSub from "pubsub-js";
import DataApi from "../api/DataApi";

/** Readonly display for a field from profile layout API (same shape as EditProfileLayout). */
function formatProfileFieldValue(field) {
  const t = (field.type || "").toLowerCase();
  if (t === "password") return "—";
  if (t === "boolean" || t === "checkbox") {
    const v = field.value;
    if (v === true || v === "true") return "Yes";
    if (v === false || v === "false") return "No";
    return "—";
  }
  const raw =
    field.displayValue != null && field.displayValue !== ""
      ? field.displayValue
      : field.value;
  if (raw === null || raw === undefined || raw === "") return "—";
  return String(raw);
}

const UserProfile = () => {
  const navigate = useNavigate();
  const [userInfo, setUserInfo] = useState({});
  const [profileSections, setProfileSections] = useState([]);
  const [profileLayoutLoading, setProfileLayoutLoading] = useState(false);
  const [profileLayoutError, setProfileLayoutError] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState({
    old: false,
    new: false,
    confirm: false,
  });
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    label: "",
    color: "danger",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      const decoded = jwt_decode(token);
      setUserInfo(decoded);
    }

    const loadUser = async () => {
      try {
        const data = await SparkApi.getLoginUserData();
        if (data) {
          setUserInfo((prev) => ({ ...prev, ...data }));
        }
      } catch (error) {
        console.error("Failed to load user profile:", error);
      }
    };

    loadUser();
  }, []);

  // Same profile layout as edit (layoutConfigType=profile); fields + sections from server
  useEffect(() => {
    if (!userInfo?.id) return undefined;
    let cancelled = false;

    const loadProfileLayout = async () => {
      setProfileLayoutLoading(true);
      setProfileLayoutError(null);
      try {
        const dataApi = new DataApi("object/edit/user");
        const idWithParams = `${userInfo.id}?layoutConfigType=profile`;
        const response = await dataApi.fetchById(idWithParams);
        const data = response?.data?.data;
        if (cancelled) return;

        if (typeof data === "string") {
          setProfileLayoutError(data);
          setProfileSections([]);
          return;
        }

        let sections = [];
        if (Array.isArray(data)) {
          sections = data;
        } else if (data?.details && Array.isArray(data.details)) {
          sections = data.details;
        } else {
          setProfileSections([]);
          return;
        }

        const seenSections = new Set();
        sections = sections.filter((section) => {
          if (seenSections.has(section.sectionName)) return false;
          seenSections.add(section.sectionName);
          return true;
        });

        sections = sections.map((section) => ({
          ...section,
          fields: (section.fields || []).filter(
            (f) => (f?.fieldName || "").toLowerCase() !== "isactive"
          ),
        }));

        setProfileSections(sections);
      } catch (err) {
        if (!cancelled) {
          setProfileLayoutError(err?.message || "Failed to load profile fields");
          setProfileSections([]);
        }
      } finally {
        if (!cancelled) setProfileLayoutLoading(false);
      }
    };

    loadProfileLayout();
    return () => {
      cancelled = true;
    };
  }, [userInfo?.id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setPasswordData({ ...passwordData, [name]: value });

    if (name === "newPassword") checkPasswordStrength(value);
  };

  const togglePasswordVisibility = (field) => {
    setShowPassword((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const checkPasswordStrength = (password) => {
    let score = 0;
    if (password.length >= 6) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[@$!%*?&]/.test(password)) score++;

    if (score === 0)
      setPasswordStrength({ score, label: "Too Weak", color: "danger" });
    else if (score === 1)
      setPasswordStrength({ score, label: "Weak", color: "warning" });
    else if (score === 2)
      setPasswordStrength({ score, label: "Good", color: "info" });
    else if (score >= 3)
      setPasswordStrength({ score, label: "Strong", color: "success" });
  };

  // ✅ Only checks new + confirm password
  const handleResetPassword = async () => {
    const { newPassword, confirmPassword } = passwordData;

    if (!newPassword || !confirmPassword) {
      PubSub.publish("RECORD_ERROR_TOAST", {
        title: "Error",
        message: "Please fill all password fields.",
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      PubSub.publish("RECORD_ERROR_TOAST", {
        title: "Error",
        message: "New and Confirm Password do not match.",
      });
      return;
    }

    if (passwordStrength.score < 3) {
      PubSub.publish("RECORD_ERROR_TOAST", {
        title: "Weak Password",
        message: "Please use a stronger password (A-Z, a-z, 0-9, symbol).",
      });
      return;
    }

    try {
      setLoading(true);
      const result = await SparkApi.resetPassword({
        newPassword: passwordData.newPassword,
        confirmPassword: passwordData.confirmPassword
      });
      if (result?.success) {
        PubSub.publish("RECORD_SAVED_TOAST", {
          title: "Success",
          message: "Password updated successfully!",
        });
        handleClosePasswordModal();
      } else {
        PubSub.publish("RECORD_ERROR_TOAST", {
          title: "Error",
          message:
            result?.errors || result?.message || "Failed to reset password.",
        });
      }
    } catch (error) {
      PubSub.publish("RECORD_ERROR_TOAST", {
        title: "Error",
        message: error.message || "Something went wrong.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClosePasswordModal = () => {
    setShowPasswordModal(false);
    setPasswordData({ oldPassword: "", newPassword: "", confirmPassword: "" });
    setPasswordStrength({ score: 0, label: "", color: "danger" });
  };

  if (!userInfo || !userInfo.username) {
    return (
      <Container className="text-center mt-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </Container>
    );
  }

  return (
    <Container
      className="py-5"
      style={{ backgroundColor: "#f8f9fc", minHeight: "100vh" }}
    >
      <Row className="justify-content-center">
        <Col md={8} lg={6}>
          <Card className="border-0 shadow-lg" style={{ borderRadius: "18px" }}>
            <Card.Body className="p-4 text-center">
              <div
                className="mx-auto mb-3 text-white rounded-circle d-flex align-items-center justify-content-center"
                style={{
                  background: "linear-gradient(135deg, #002B5B, #0056b3)",
                  width: 90,
                  height: 90,
                  fontSize: 32,
                  fontWeight: "600",
                }}
              >
                {userInfo.firstname
                  ? userInfo.firstname.charAt(0).toUpperCase()
                  : userInfo.username?.charAt(0).toUpperCase()}
              </div>

              <h3 className="mb-1" style={{ color: "#002B5B", fontWeight: 600 }}>
                {userInfo.firstname} {userInfo.lastname}
              </h3>
              <p className="text-muted mb-4">{userInfo.email}</p>

              <div className="text-start ps-3">
                <p className="mb-2">
                  <FaUser className="me-2 text-primary" />{" "}
                  <strong>Username:</strong> {userInfo.username}
                </p>

                {profileLayoutLoading && profileSections.length === 0 && (
                  <div className="text-muted py-2">
                    <div
                      className="spinner-border spinner-border-sm text-primary me-2"
                      role="status"
                    />
                    Loading profile fields…
                  </div>
                )}

                {profileLayoutError && (
                  <p className="text-danger small mb-2">{profileLayoutError}</p>
                )}

                {!profileLayoutLoading &&
                  profileSections.length > 0 &&
                  profileSections.map((section) => (
                    <div key={section.sectionName} className="mb-3">
                      {(section.sectionLabel || section.sectionName) && (
                        <h6
                          className="text-uppercase text-muted small mb-2"
                          style={{ letterSpacing: "0.04em" }}
                        >
                          {section.sectionLabel || section.sectionName}
                        </h6>
                      )}
                      {(section.fields || []).map((field) => (
                        <p key={field.fieldName} className="mb-2">
                          <strong>{field.label || field.fieldName}:</strong>{" "}
                          <span className="text-break">
                            {formatProfileFieldValue(field)}
                          </span>
                        </p>
                      ))}
                    </div>
                  ))}

                {!profileLayoutLoading &&
                  userInfo?.id &&
                  profileSections.length === 0 &&
                  !profileLayoutError && (
                    <div className="text-muted small">
                      No profile fields are configured for this layout.
                    </div>
                  )}

                {!profileLayoutLoading &&
                  profileSections.length === 0 &&
                  profileLayoutError && (
                    <div className="text-muted small mt-2">
                      <p className="mb-1">
                        <strong>Email:</strong> {userInfo.email || "—"}
                      </p>
                      <p className="mb-1">
                        <strong>Phone:</strong> {userInfo.phone || "—"}
                      </p>
                      <p className="mb-1">
                        <strong>Company:</strong> {userInfo.companyname || "—"}
                      </p>
                      <p className="mb-0">
                        <strong>Role:</strong>{" "}
                        {userInfo.role_name || userInfo.userrole || "—"}
                      </p>
                    </div>
                  )}
              </div>

              <div className="mt-4 d-flex gap-2 justify-content-center flex-wrap">
                <Button
                  variant="outline-primary"
                  onClick={() => navigate("/profile/edit")}
                  style={{
                    borderColor: "#002B5B",
                    color: "#002B5B",
                    padding: "10px 24px",
                    borderRadius: "10px",
                    fontWeight: 500,
                  }}
                >
                  Edit Profile
                </Button>
                <Button
                  variant="primary"
                  onClick={() => setShowPasswordModal(true)}
                  style={{
                    backgroundColor: "#002B5B",
                    borderColor: "#002B5B",
                    padding: "10px 24px",
                    borderRadius: "10px",
                    fontWeight: 500,
                  }}
                >
                  <FaLock className="me-2 text-white" />
                  Reset Password
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* 🔹 Reset Password Modal */}
      <Modal show={showPasswordModal} onHide={handleClosePasswordModal} centered>
        <Modal.Header
          closeButton
          style={{ backgroundColor: "#002B5B" }}
        >

          <Modal.Title className="text-white">
            <FaLock className="me-2" /> Reset Password
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Old Password (optional)</Form.Label>
              <InputGroup>
                <Form.Control
                  type={showPassword.old ? "text" : "password"}
                  name="oldPassword"
                  value={passwordData.oldPassword}
                  onChange={handleChange}
                  placeholder="Enter old password (optional)"
                />
                <Button
                  variant="outline-secondary"
                  onClick={() => togglePasswordVisibility("old")}
                >
                  {showPassword.old ? <FaEyeSlash /> : <FaEye />}
                </Button>
              </InputGroup>
            </Form.Group>

            {/* New Password */}
            <Form.Group className="mb-3">
              <Form.Label>New Password</Form.Label>
              <InputGroup>
                <Form.Control
                  type={showPassword.new ? "text" : "password"}
                  name="newPassword"
                  value={passwordData.newPassword}
                  onChange={handleChange}
                  placeholder="Enter new password"
                />
                <Button
                  variant="outline-secondary"
                  onClick={() => togglePasswordVisibility("new")}
                >
                  {showPassword.new ? <FaEyeSlash /> : <FaEye />}
                </Button>
              </InputGroup>

              {passwordData.newPassword && (
                <div className="mt-2 d-flex align-items-center">
                  <ProgressBar
                    now={(passwordStrength.score / 4) * 100}
                    variant={passwordStrength.color}
                    style={{ flex: 1, height: "8px", borderRadius: "10px" }}
                  />
                  <span
                    className={`ms-3 text-${passwordStrength.color} fw-semibold`}
                    style={{ minWidth: 80 }}
                  >
                    {passwordStrength.label}{" "}
                    {passwordStrength.color === "success" ? (
                      <FaCheckCircle />
                    ) : (
                      <FaExclamationTriangle />
                    )}
                  </span>
                </div>
              )}
            </Form.Group>

            {/* Confirm Password */}
            <Form.Group>
              <Form.Label>Confirm Password</Form.Label>
              <InputGroup>
                <Form.Control
                  type={showPassword.confirm ? "text" : "password"}
                  name="confirmPassword"
                  value={passwordData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Confirm new password"
                />
                <Button
                  variant="outline-secondary"
                  onClick={() => togglePasswordVisibility("confirm")}
                >
                  {showPassword.confirm ? <FaEyeSlash /> : <FaEye />}
                </Button>
              </InputGroup>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={handleClosePasswordModal}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleResetPassword}
            disabled={loading}
            style={{ backgroundColor: "#002B5B", borderColor: "#002B5B" }}
          >
            {loading ? "Saving..." : "Save Changes"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default UserProfile;


// import React, { useEffect, useState } from "react";
// import { Container, Row, Col, Card, Button, Form, Modal } from "react-bootstrap";
// import { FaUser, FaEnvelope, FaPhone, FaBuilding, FaUserShield, FaLock } from "react-icons/fa";
// import jwt_decode from "jwt-decode";
// import SparkApi from "../api/SparkApi";
// import PubSub from "pubsub-js";

// const UserProfile = () => {
//   const [userInfo, setUserInfo] = useState({});
//   const [showPasswordModal, setShowPasswordModal] = useState(false);
//   const [passwordData, setPasswordData] = useState({ oldPassword: "", newPassword: "", confirmPassword: "" });
//   const [loading, setLoading] = useState(false);

//   // ✅ Load user info from token
//   useEffect(() => {
//     const token = localStorage.getItem("token");
//     if (token) {
//       const decoded = jwt_decode(token);
//       setUserInfo(decoded);
//     }
//   }, []);

//   // ✅ Handle password input change
//   const handleChange = (e) => {
//     setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
//   };

//   // ✅ Handle password reset submit
//   const handleResetPassword = async () => {
//     const { oldPassword, newPassword, confirmPassword } = passwordData;

//     if (!oldPassword || !newPassword || !confirmPassword) {
//       PubSub.publish("RECORD_ERROR_TOAST", {
//         title: "Error",
//         message: "Please fill all password fields.",
//       });
//       return;
//     }

//     if (newPassword !== confirmPassword) {
//       PubSub.publish("RECORD_ERROR_TOAST", {
//         title: "Error",
//         message: "New and Confirm Password do not match.",
//       });
//       return;
//     }

//     try {
//       setLoading(true);
//       const result = await SparkApi.resetPassword({
//         user_id: userInfo.id,
//         old_password: oldPassword,
//         new_password: newPassword,
//       });

//       if (result?.success) {
//         PubSub.publish("RECORD_SAVED_TOAST", {
//           title: "Success",
//           message: "Password updated successfully!",
//         });
//         setShowPasswordModal(false);
//         setPasswordData({ oldPassword: "", newPassword: "", confirmPassword: "" });
//       } else {
//         PubSub.publish("RECORD_ERROR_TOAST", {
//           title: "Error",
//           message: result?.message || "Failed to reset password.",
//         });
//       }
//     } catch (error) {
//       PubSub.publish("RECORD_ERROR_TOAST", {
//         title: "Error",
//         message: error.message || "Something went wrong.",
//       });
//     } finally {
//       setLoading(false);
//     }
//   };

//   if (!userInfo || !userInfo.username) {
//     return (
//       <Container className="text-center mt-5">
//         <div className="spinner-border text-primary" role="status">
//           <span className="visually-hidden">Loading...</span>
//         </div>
//       </Container>
//     );
//   }

//   return (
//     <Container className="py-5">
//       <Row className="justify-content-center">
//         <Col md={8} lg={6}>
//           <Card className="shadow-lg border-0">
//             <Card.Body className="p-4 text-center">
//               <div className="profile-avatar-large mx-auto mb-3 bg-primary text-white rounded-circle d-flex align-items-center justify-content-center" style={{ width: 80, height: 80, fontSize: 30 }}>
//                 {userInfo.firstname
//                   ? userInfo.firstname.charAt(0).toUpperCase()
//                   : userInfo.username?.charAt(0).toUpperCase()}
//               </div>

//               <h3 className="mb-1">{userInfo.firstname} {userInfo.lastname}</h3>
//               <p className="text-muted mb-3">{userInfo.email}</p>

//               <div className="text-start mt-4">
//                 <p><FaUser className="me-2 text-primary" /> Username: {userInfo.username}</p>
//                 <p><FaEnvelope className="me-2 text-primary" /> Email: {userInfo.email}</p>
//                 <p><FaPhone className="me-2 text-primary" /> Phone: {userInfo.phone || "-"}</p>
//                 <p><FaBuilding className="me-2 text-primary" /> Company: {userInfo.companyname || "-"}</p>
//                 <p><FaUserShield className="me-2 text-primary" /> Role: {userInfo.role_name || userInfo.userrole}</p>
//               </div>

//               <div className="mt-4">
//                 <Button variant="warning" onClick={() => setShowPasswordModal(true)}>
//                   <FaLock className="me-2" />
//                   Reset Password
//                 </Button>
//               </div>
//             </Card.Body>
//           </Card>
//         </Col>
//       </Row>

//       {/* ✅ Reset Password Modal */}
//       <Modal show={showPasswordModal} onHide={() => setShowPasswordModal(false)} centered>
//         <Modal.Header closeButton>
//           <Modal.Title><FaLock className="me-2" /> Reset Password</Modal.Title>
//         </Modal.Header>
//         <Modal.Body>
//           <Form>
//             <Form.Group className="mb-3">
//               <Form.Label>Old Password</Form.Label>
//               <Form.Control
//                 type="password"
//                 name="oldPassword"
//                 value={passwordData.oldPassword}
//                 onChange={handleChange}
//                 placeholder="Enter old password"
//               />
//             </Form.Group>

//             <Form.Group className="mb-3">
//               <Form.Label>New Password</Form.Label>
//               <Form.Control
//                 type="password"
//                 name="newPassword"
//                 value={passwordData.newPassword}
//                 onChange={handleChange}
//                 placeholder="Enter new password"
//               />
//             </Form.Group>

//             <Form.Group>
//               <Form.Label>Confirm Password</Form.Label>
//               <Form.Control
//                 type="password"
//                 name="confirmPassword"
//                 value={passwordData.confirmPassword}
//                 onChange={handleChange}
//                 placeholder="Confirm new password"
//               />
//             </Form.Group>
//           </Form>
//         </Modal.Body>
//         <Modal.Footer>
//           <Button variant="secondary" onClick={() => setShowPasswordModal(false)}>
//             Cancel
//           </Button>
//           <Button variant="primary" onClick={handleResetPassword} disabled={loading}>
//             {loading ? "Saving..." : "Save Changes"}
//           </Button>
//         </Modal.Footer>
//       </Modal>
//     </Container>
//   );
// };

// export default UserProfile;
